import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ApplicationForm from '../components/ApplicationForm';
import { useAuth } from '../contexts/AuthContext';

const FundDetail = () => {
  const { fundId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [fund, setFund] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplicationForm, setShowApplicationForm] = useState(searchParams.get('apply') === 'true');

  useEffect(() => {
    fetchFundDetails();
  }, [fundId]);

  // Redirect non-logged-in users to login
  useEffect(() => {
    if (!authLoading && !user) {
      navigate(`/login?redirect=/funding-options/${fundId}`);
    }
  }, [user, authLoading, navigate, fundId]);

  const fetchFundDetails = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/funding-options/${fundId}`);
      setFund(data);
    } catch (error) {
      console.error('Error fetching fund details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyClick = () => {
    if (!user) {
      navigate('/login?redirect=/funding-options/' + fundId + '?apply=true');
    } else if (!user.has_active_subscription) {
      // Redirect to membership page for free users
      navigate('/membership');
    } else {
      setShowApplicationForm(true);
    }
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      'Impact': 'bg-blue-100 text-blue-800',
      'Climate': 'bg-green-100 text-green-800',
      'Hybrid': 'bg-purple-100 text-purple-800',
      'Emerging': 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const formatTicketSize = (min, max) => {
    if (!min && !max) return 'Not specified';
    if (min && max) return `₹${min} Lakhs - ₹${max} Lakhs`;
    if (min) return `From ₹${min} Lakhs`;
    if (max) return `Up to ₹${max} Lakhs`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading fund details...</p>
        </div>
      </div>
    );
  }

  if (!fund) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">Fund Not Found</h2>
          <p className="text-gray-600 mb-6">The funding option you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/funding-options')}>
            Browse All Funds
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-12">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate('/funding-options')}
            className="text-gray-300 hover:text-white mb-4 flex items-center text-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to All Funds
          </button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="mb-3">
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getCategoryBadgeColor(fund.category)}`}>
                  {fund.category}
                </span>
              </div>
              <h1 className="text-4xl font-bold mb-2">{fund.fund_name}</h1>
              <p className="text-xl text-gray-300">{fund.description}</p>
            </div>
            <Button
              onClick={handleApplyClick}
              className="bg-[#E87722] hover:bg-[#d16b1a] text-white px-8 py-3 text-lg"
            >
              Apply Through CSED
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Investment Focus */}
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">Investment Focus Areas</h2>
              <div className="flex flex-wrap gap-3">
                {fund.investment_focus_areas.map((area, idx) => (
                  <span key={idx} className="bg-[#E87722] bg-opacity-10 text-[#E87722] px-4 py-2 rounded-lg font-medium">
                    {area}
                  </span>
                ))}
              </div>
            </Card>

            {/* Key Sectors */}
            <Card className="p-6">
              <h2 className="text-2xl font-bold mb-4">Key Sectors</h2>
              <div className="grid md:grid-cols-2 gap-3">
                {fund.key_sectors.map((sector, idx) => (
                  <div key={idx} className="flex items-center p-3 bg-gray-50 rounded-lg">
                    <svg className="w-5 h-5 text-[#E87722] mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-gray-700">{sector}</span>
                  </div>
                ))}
              </div>
            </Card>

            {/* ESG & Impact Criteria */}
            {fund.esg_impact_criteria && (
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-4">ESG & Impact Criteria</h2>
                <p className="text-gray-700 leading-relaxed">{fund.esg_impact_criteria}</p>
              </Card>
            )}

            {/* Portfolio Highlights */}
            {fund.portfolio_highlights && fund.portfolio_highlights.length > 0 && (
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-4">Portfolio Highlights</h2>
                <ul className="space-y-3">
                  {fund.portfolio_highlights.map((highlight, idx) => (
                    <li key={idx} className="flex items-start">
                      <svg className="w-5 h-5 text-[#E87722] mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Submission Process */}
            {fund.submission_process && (
              <Card className="p-6 bg-blue-50 border-blue-200">
                <h2 className="text-2xl font-bold mb-4 text-blue-900">How to Apply</h2>
                <p className="text-gray-700 leading-relaxed mb-4">{fund.submission_process}</p>
                <div className="bg-white p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Apply through CSED for:</strong>
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>✓ Expert application review</li>
                    <li>✓ Pitch refinement support</li>
                    <li>✓ Direct connection to fund managers</li>
                    <li>✓ Post-submission guidance</li>
                  </ul>
                </div>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Facts */}
            <Card className="p-6">
              <h3 className="text-lg font-bold mb-4">Quick Facts</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Investment Stage</p>
                  <div className="flex flex-wrap gap-2">
                    {fund.stage.map((s, idx) => (
                      <span key={idx} className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm font-medium">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Ticket Size</p>
                  <p className="font-semibold text-gray-900">{formatTicketSize(fund.ticket_size_min, fund.ticket_size_max)}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-1">Geography Focus</p>
                  <div className="flex flex-wrap gap-2">
                    {fund.geography_focus.map((geo, idx) => (
                      <span key={idx} className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                        {geo}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Card>

            {/* Contact */}
            {(fund.website_url || fund.contact_email) && (
              <Card className="p-6">
                <h3 className="text-lg font-bold mb-4">Contact Information</h3>
                <div className="space-y-3">
                  {fund.website_url && (
                    <a
                      href={fund.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-[#E87722] hover:underline"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                      </svg>
                      Visit Website
                    </a>
                  )}
                  {fund.contact_email && (
                    <a
                      href={`mailto:${fund.contact_email}`}
                      className="flex items-center text-[#E87722] hover:underline"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {fund.contact_email}
                    </a>
                  )}
                </div>
              </Card>
            )}

            {/* CTA */}
            {user && (
              <Card className="p-6 bg-gradient-to-br from-[#E87722] to-[#d16b1a] text-white">
                {!user.has_active_subscription ? (
                  // Registered user - no subscription
                  <>
                    <h3 className="text-xl font-bold mb-3">Want to Apply?</h3>
                    <p className="text-sm mb-4 text-white text-opacity-90">
                      Subscribe to a membership plan to apply for funding opportunities and get expert support throughout the process.
                    </p>
                    <Link to="/membership">
                      <Button className="w-full bg-white text-[#E87722] hover:bg-gray-100">
                        View Membership Plans
                      </Button>
                    </Link>
                  </>
                ) : (
                  // Subscribed member - full access
                  <>
                    <h3 className="text-xl font-bold mb-3">Ready to Apply?</h3>
                    <p className="text-sm mb-4 text-white text-opacity-90">
                      Submit your application through CSED and get expert support throughout the process.
                    </p>
                    <Button
                      onClick={handleApplyClick}
                      className="w-full bg-white text-[#E87722] hover:bg-gray-100"
                    >
                      Start Application
                    </Button>
                  </>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* Application Form Modal - Only for subscribed members */}
      {showApplicationForm && user && user.has_active_subscription && (
        <ApplicationForm
          fund={fund}
          onClose={() => setShowApplicationForm(false)}
          onSuccess={() => {
            setShowApplicationForm(false);
            navigate('/dashboard?tab=applications');
          }}
        />
      )}
    </div>
  );
};

export default FundDetail;
